import { useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import type { ChannelType } from '@buffer-mono/popcorn'

import { ReactionPicker } from '../ReactionPicker'
import { ReactionIcon } from './ReactionIcon'
import type {
  ReactionType,
  SupportedReaction,
} from '../../hooks/configuration/useConfigurationQuery'
import {
  FirstTimeLikeCoachmark,
  useFirstTimeLikeCoachmark,
} from '../FirstTimeLikeCoachmark'

import styles from './APIReaction.module.css'

type MultiReactionsProps = {
  likingIcon?: string | null
  supportedReactions: SupportedReaction[]
  /** Whether the engagement carries a reaction, read from its data. */
  isReacted: boolean
  /** The reaction the engagement carries, used to render the chosen visual. */
  currentReactionType?: ReactionType
  /** Whether a reaction mutation is in flight. */
  loading?: boolean
  /**
   * Applies or removes the reaction. Must throw on failure so the optimistic
   * state rolls back — the caller owns the error copy for its engagement.
   */
  onReact: (reactionType: ReactionType, isActive: boolean) => Promise<void>
  /**
   * Noun in the icon label, and the interaction-guard attribute the card
   * reads to keep a picker click from expanding it.
   */
  entity: 'comment' | 'mention'
  /** Source network, used by the first-time like coachmark illustration. */
  service?: ChannelType
  /** Display name of the source network, shown in the picker's sync caveat. */
  networkName?: string
  targetLabel?: string
  /**
   * When true, the existing reaction is still rendered but the picker
   * doesn't open and the click handler is a no-op. Used to surface the
   * current reaction state to view-only Community actors.
   */
  disabled?: boolean
  /** Tooltip explaining why the control is inert, shown when `disabled`. */
  viewOnlyTooltip?: string
}

const SHOW_PICKER_DELAY_MS = 100
const CLOSE_PICKER_DELAY_MS = 300
const CLOSE_ANIMATION_DURATION_MS = 150

// Coordinates open state across all MultiReactions instances.
// Ensures only one picker is visible at a time, and allows switching
// between instances without a gap.
let nextPickerId = 0
let activePickerIdModule = -1
let activePickerClose: (() => void) | null = null

export const MultiReactions = ({
  likingIcon,
  supportedReactions,
  isReacted,
  currentReactionType,
  loading = false,
  onReact,
  entity,
  service,
  networkName,
  targetLabel,
  disabled = false,
  viewOnlyTooltip,
}: MultiReactionsProps): JSX.Element => {
  const coachmark = useFirstTimeLikeCoachmark(service ?? '')

  const [optimisticIsReacted, setOptimisticIsReacted] = useState(isReacted)
  const [optimisticReactionType, setOptimisticReactionType] = useState<
    ReactionType | undefined
  >(currentReactionType)
  const isMutatingRef = useRef(false)

  const [showPicker, setShowPicker] = useState(false)
  const [pickerAnimateIn, setPickerAnimateIn] = useState(false)
  // Only move focus into the picker when it was opened via keyboard —
  // hover-driven opens should leave focus on the trigger.
  const [autoFocusPicker, setAutoFocusPicker] = useState(false)
  const showPickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closePickerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeAnimationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Stable per-instance ID — lazy initializer runs exactly once
  const [myId] = useState(() => nextPickerId++)

  useEffect(() => {
    if (!isMutatingRef.current) {
      setOptimisticIsReacted(isReacted)
      setOptimisticReactionType(currentReactionType)
    }
  }, [isReacted, currentReactionType])

  useEffect(() => {
    return (): void => {
      if (showPickerTimerRef.current) clearTimeout(showPickerTimerRef.current)
      if (closePickerTimerRef.current) clearTimeout(closePickerTimerRef.current)
      if (closeAnimationTimerRef.current)
        clearTimeout(closeAnimationTimerRef.current)
      // Release the global lock if this instance was the active picker on unmount
      if (activePickerIdModule === myId) {
        activePickerIdModule = -1
        activePickerClose = null
      }
    }
  }, [myId])

  const handleReactionMutation = async (
    reactionType: ReactionType,
    isActive: boolean,
  ): Promise<void> => {
    if (isMutatingRef.current) return
    isMutatingRef.current = true
    setOptimisticIsReacted(isActive)
    setOptimisticReactionType(isActive ? reactionType : undefined)

    try {
      await onReact(reactionType, isActive)
      // Explain one-way sync the first time the actor reacts on this network
      // (only when adding). The once-per-network dismissal caps repeats, so no
      // need to also gate on the card being expanded.
      if (isActive) {
        coachmark.trigger()
      }
    } catch {
      setOptimisticIsReacted(isReacted)
      setOptimisticReactionType(currentReactionType)
    } finally {
      isMutatingRef.current = false
    }
  }

  /**
   * Triggered when the user selects a reaction from the picker.
   * Closes the picker and sets the chosen reaction as active.
   * Always sets isActive to true — switching reactions is handled
   * by the API replacing the previous one.
   */
  const handleReactionSelect = (reactionType: ReactionType): void => {
    closePicker()
    void handleReactionMutation(reactionType, true)
  }

  /**
   * Triggered when the user activates the reaction icon directly (click or
   * keyboard). If already reacted, removes the current reaction. If not
   * reacted, opens the picker so the reaction can be chosen — this is the
   * keyboard and touch entry point, since those can't rely on hover.
   */
  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation()

    // View-only: surface the existing reaction but don't let the actor
    // toggle it off. Tooltip explains why on hover (set on ReactionIcon).
    if (disabled) return

    // Read the optimistic state, not the prop — it is what the icon renders.
    // A reaction that has settled but not yet reached the caller's data would
    // otherwise reopen the picker instead of coming off.
    if (optimisticIsReacted) {
      const reactionType = optimisticReactionType ?? currentReactionType
      if (reactionType) {
        closePicker()
        void handleReactionMutation(reactionType, false)
      }
      return
    }

    // Keyboard-activated clicks report a detail of 0; move focus into the
    // picker in that case so the reactions are reachable without a pointer.
    const openedViaKeyboard = e.detail === 0
    if (showPicker) {
      closePicker()
    } else {
      openPicker(openedViaKeyboard)
    }
  }

  const cancelAllTimers = (): void => {
    if (showPickerTimerRef.current) {
      clearTimeout(showPickerTimerRef.current)
      showPickerTimerRef.current = null
    }
    if (closePickerTimerRef.current) {
      clearTimeout(closePickerTimerRef.current)
      closePickerTimerRef.current = null
    }
    if (closeAnimationTimerRef.current) {
      clearTimeout(closeAnimationTimerRef.current)
      closeAnimationTimerRef.current = null
    }
  }

  const openPicker = (focusOnOpen = false): void => {
    cancelAllTimers()
    // Close any other instance's picker (closePicker defined below — accessible at call time)
    activePickerClose?.()
    // Register this instance as the active picker
    activePickerIdModule = myId
    activePickerClose = closePicker
    setAutoFocusPicker(focusOnOpen)
    setShowPicker(true)
    setPickerAnimateIn(true)
  }

  const closePicker = (): void => {
    // Only release the global lock if we're still the registered active picker
    if (activePickerIdModule === myId) {
      activePickerIdModule = -1
      activePickerClose = null
    }
    setPickerAnimateIn(false)
    closeAnimationTimerRef.current = setTimeout(() => {
      setShowPicker(false)
    }, CLOSE_ANIMATION_DURATION_MS)
  }

  const scheduleShowPicker = (): void => {
    // If our picker is already open, just cancel any pending close and stay open
    if (activePickerIdModule === myId) {
      cancelAllTimers()
      return
    }
    // Close any other picker immediately so the transition feels snappy
    activePickerClose?.()
    // Cancel any pending open/close timers for this instance
    if (showPickerTimerRef.current) clearTimeout(showPickerTimerRef.current)
    if (closePickerTimerRef.current) {
      clearTimeout(closePickerTimerRef.current)
      closePickerTimerRef.current = null
    }
    // Open after sustained hover
    showPickerTimerRef.current = setTimeout(() => {
      openPicker()
    }, SHOW_PICKER_DELAY_MS)
  }

  const scheduleClosePicker = (): void => {
    // Cancel any pending show timer — user left before hover delay elapsed
    if (showPickerTimerRef.current) {
      clearTimeout(showPickerTimerRef.current)
      showPickerTimerRef.current = null
    }
    if (closePickerTimerRef.current) {
      clearTimeout(closePickerTimerRef.current)
    }
    closePickerTimerRef.current = setTimeout(() => {
      closePicker()
    }, CLOSE_PICKER_DELAY_MS)
  }

  // Used only for the picker content's onMouseLeave. Unlike scheduleClosePicker,
  // this does NOT cancel the show timer — if the picker is already closing after
  // a reaction selection, the onMouseLeave from the animating-out content should
  // not discard a pending re-open triggered by the user hovering the icon again.
  const handlePickerContentMouseLeave = (): void => {
    if (activePickerIdModule !== myId) return
    scheduleClosePicker()
  }

  const content = (
    <div
      data-comment-reaction={entity === 'comment' ? '' : undefined}
      data-mention-reaction={entity === 'mention' ? '' : undefined}
      className={styles.container}
    >
      <ReactionPicker
        supportedReactions={supportedReactions}
        networkName={networkName}
        onReactionSelect={handleReactionSelect}
        open={showPicker}
        animateIn={pickerAnimateIn}
        autoFocusOnOpen={autoFocusPicker}
        onOpenChange={(open): void => {
          if (!open) closePicker()
        }}
        onMouseEnter={cancelAllTimers}
        onMouseLeave={handlePickerContentMouseLeave}
      >
        <ReactionIcon
          likingIcon={likingIcon}
          isLiked={optimisticIsReacted}
          reactionType={optimisticReactionType}
          supportedReactions={supportedReactions}
          entity={entity}
          targetLabel={targetLabel}
          loading={loading}
          active={showPicker}
          disabled={disabled}
          tooltipOverride={disabled ? viewOnlyTooltip : undefined}
          onClick={handleClick}
          onMouseEnter={disabled ? undefined : scheduleShowPicker}
          onMouseLeave={disabled ? undefined : scheduleClosePicker}
        />
      </ReactionPicker>
    </div>
  )

  if (!service || !networkName) {
    return content
  }

  return (
    <FirstTimeLikeCoachmark
      open={coachmark.open}
      onDismiss={coachmark.dismiss}
      service={service}
      networkName={networkName}
    >
      {content}
    </FirstTimeLikeCoachmark>
  )
}
